/**
 * Hand-coded work and workplace situational phrases
 * (ESL → Japanese L1 kana).
 * License: CC BY-SA 4.0.
 *
 * #1 motivation for adult eikaiwa learners. Includes ladder marquees
 * `ask-help` and `apologize-late` at A2/B1/B2.
 */

import type { SituationCard } from '../../situations/types.js';

const workWorkplace: SituationCard[] = [
  // Ladder marquee — asking for help across registers
  {
    id: 'work.ask-help.a2',
    situationId: 'work.ask-help.a2',
    front: 'Can you help me with this?',
    back: 'これ、手伝ってもらえますか？',
    category: 'work-workplace',
    cefr: 'A2',
    translations: { ja: 'これ、てつだってもらえますか？' },
    speechAct: 'request',
    situationFamily: 'ask-help',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'work.ask-help.b1',
    situationId: 'work.ask-help.b1',
    front: "Sorry to bother you — would you have a minute to look at this with me?",
    back: 'お忙しいところすみません、一緒に見ていただける時間ありますか？',
    category: 'work-workplace',
    cefr: 'B1',
    translations: { ja: 'おいそがしいところすみません、いっしょにみていただけるじかんありますか？' },
    speechAct: 'request',
    situationFamily: 'ask-help',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: 'B1 request pattern: apologetic opener + soft request. "Sorry to bother you" is the canonical workplace softener.',
      ja: '職場でのB1依頼パターン：「お忙しいところすみません」のような前置きを入れるとやわらかくなります。'
    }
  },
  {
    id: 'work.ask-help.b2',
    situationId: 'work.ask-help.b2',
    front: "I was wondering if you might be able to walk me through this when you have a chance — no rush.",
    back: 'お時間のあるときに、これを一緒に確認していただけたらと思いまして。急ぎではありません。',
    category: 'work-workplace',
    cefr: 'B2',
    translations: { ja: 'おじかんのあるときに、これをいっしょにかくにんしていただけたらとおもいまして。いそぎではありません。' },
    speechAct: 'request',
    situationFamily: 'ask-help',
    formality: 'polite',
    channel: 'email',
    notes: {
      en: 'B2 hedged request stack: "I was wondering" + "if you might be able to" + "when you have a chance" + "no rush" — four softeners. Standard for asking favors of senior colleagues.',
      ja: 'B2の依頼は「I was wondering」「might be able to」「when you have a chance」「no rush」と複数のクッションを重ねます。'
    }
  },
  // Ladder marquee — apologizing for being late
  {
    id: 'work.apologize-late.a2',
    situationId: 'work.apologize-late.a2',
    front: "Sorry I'm late.",
    back: '遅れてすみません。',
    category: 'work-workplace',
    cefr: 'A2',
    translations: { ja: 'おくれてすみません。' },
    speechAct: 'apology',
    situationFamily: 'apologize-late',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'work.apologize-late.b1',
    situationId: 'work.apologize-late.b1',
    front: "Sorry I'm late — the train was held up. Won't happen again.",
    back: '遅れて申し訳ありません。電車が遅延していて……今後は気をつけます。',
    category: 'work-workplace',
    cefr: 'B1',
    translations: { ja: 'おくれてもうしわけありません。でんしゃがちえんしていて……こんごはきをつけます。' },
    speechAct: 'apology',
    situationFamily: 'apologize-late',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: 'English apology: regret + reason + future commitment. Skipping the reason can read as evasive; over-apologizing ("I\'m so so sorry") feels excessive.',
      ja: '英語の謝罪は「お詫び＋理由＋今後の約束」の三点セット。日本式に何度も繰り返し謝ると過剰に感じられます。'
    }
  },
  {
    id: 'work.apologize-late.b2',
    situationId: 'work.apologize-late.b2',
    front: "I really apologize for keeping you waiting — there was a holdup on the train, and I should have left earlier to give myself a buffer. It won't happen again.",
    back: 'お待たせして本当に申し訳ありません。電車のトラブルがあったのですが、もっと余裕を持って家を出るべきでした。今後はこのようなことのないようにします。',
    category: 'work-workplace',
    cefr: 'B2',
    translations: { ja: 'おまたせしてほんとうにもうしわけありません。でんしゃのトラブルがあったのですが、もっとよゆうをもっていえをでるべきでした。こんごはこのようなことのないようにします。' },
    speechAct: 'apology',
    situationFamily: 'apologize-late',
    formality: 'formal',
    channel: 'in-person',
    notes: {
      en: 'B2 apology: takes ownership ("I should have…") rather than blaming circumstances. Common in professional / client contexts.',
      ja: 'B2の謝罪は状況のせいにせず、「自分がもっと余裕を持つべきだった」と責任を引き受けるのが特徴。ビジネスや顧客対応で使われます。'
    }
  },
  {
    id: 'work.email-opening',
    situationId: 'work.email-opening',
    front: 'Hi Sarah, hope you\'re doing well. I wanted to follow up on…',
    back: 'サラさん、お元気ですか。先日の件についてご連絡しました……',
    category: 'work-workplace',
    cefr: 'B1',
    translations: { ja: 'サラさん、おげんきですか。せんじつのけんについてごれんらくしました……' },
    speechAct: 'opening',
    formality: 'polite',
    channel: 'email',
    pragmaticNote: {
      en: 'English business email is casual-leaning by default. Skip "Dear" + "I hope this email finds you well" — those read as overly formal in 2026.',
      ja: '英語のビジネスメールは2026年現在カジュアル寄りが標準。"Dear" や "I hope this email finds you well" は古めかしい印象。'
    }
  },
  {
    id: 'work.email-closing',
    situationId: 'work.email-closing',
    front: 'Let me know if you have any questions. Thanks!',
    back: '何かご質問があればお知らせください。よろしくお願いします。',
    category: 'work-workplace',
    cefr: 'B1',
    translations: { ja: 'なにかごしつもんがあればおしらせください。よろしくおねがいします。' },
    speechAct: 'closing',
    formality: 'polite',
    channel: 'email'
  },
  {
    id: 'work.disagree-meeting',
    situationId: 'work.disagree-meeting',
    front: "I see where you\'re coming from, but I'd push back a little on that — here\'s why.",
    back: 'おっしゃることはわかるのですが、その点は少し違う見方をしています。理由を説明させてください。',
    category: 'work-workplace',
    cefr: 'B2',
    translations: { ja: 'おっしゃることはわかるのですが、そのてんはすこしちがうみかたをしています。りゆうをせつめいさせてください。' },
    speechAct: 'disagreement',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: '"Push back" is workplace English for "respectfully disagree." Pair with "I see where you\'re coming from" + "here\'s why" to keep it constructive.',
      ja: '"Push back"は職場の英語で「丁重に反対する」の意味。「相手の理解＋理由の提示」とセットで使うと建設的に響きます。'
    }
  },
  {
    id: 'work.deadline-extension',
    situationId: 'work.deadline-extension',
    front: "I hate to ask, but is there any flexibility on Friday's deadline? I want to make sure I do this right.",
    back: '恐縮ですが、金曜日の締め切りに少し融通は利きますか？きちんと仕上げたくて。',
    category: 'work-workplace',
    cefr: 'B2',
    translations: { ja: 'きょうしゅくですが、きんようびのしめきりにすこしゆうずうはききますか？きちんとしあげたくて。' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'email'
  },
  {
    id: 'work.thanks-help',
    situationId: 'work.thanks-help',
    front: 'Thanks so much for jumping in on this — really appreciate it.',
    back: '対応してくれて本当にありがとう。とても助かりました。',
    category: 'work-workplace',
    cefr: 'B1',
    translations: { ja: 'たいおうしてくれてほんとうにありがとう。とてもたすかりました。' },
    speechAct: 'gratitude',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'work.intro-meeting',
    situationId: 'work.intro-meeting',
    front: 'For those who haven\'t met me yet, I\'m the new project lead — happy to be here.',
    back: 'まだお会いしていない方もいらっしゃるかと思いますが、新しくプロジェクトを担当することになりました。よろしくお願いします。',
    category: 'work-workplace',
    cefr: 'C1',
    translations: { ja: 'まだおあいしていないかたもいらっしゃるかとおもいますが、あたらしくプロジェクトをたんとうすることになりました。よろしくおねがいします。' },
    speechAct: 'opening',
    formality: 'formal',
    channel: 'in-person'
  },
  {
    id: 'work.diplomatic-feedback',
    situationId: 'work.diplomatic-feedback',
    front: "This is really solid — one thing I\'d gently flag is the timeline assumption on slide three.",
    back: 'とてもよくできていますね。一点だけ気になったのは、三枚目のスケジュールの前提条件です。',
    category: 'work-workplace',
    cefr: 'C1',
    translations: { ja: 'とてもよくできていますね。いってんだけきになったのは、さんまいめのスケジュールのぜんていじょうけんです。' },
    speechAct: 'disagreement',
    formality: 'formal',
    channel: 'in-person',
    notes: {
      en: 'C1 feedback structure: positive opener + softener ("gently flag") + specific point. Models the diplomatic-disagreement skill at the highest end of B2/C1.',
      ja: 'C1のフィードバックは「肯定→ぼかしの一語→具体的な指摘」の流れ。B2の上位〜C1で求められる外交的な異議の伝え方。'
    }
  }
];

export default workWorkplace;
